<template>
  <div id="app">
    <div
      v-if="isDangerous"
      class="dangerous">
      <span class="dangerous__text">!</span>
    </div>

    <!-- logo with text -->
    <div class="center-wrapper">
      <center :show-text="!isLoading && isNotEmpty(unitsCounter)" label="Units" :value="unitsCounter.currentValue" />
    </div>

    <div class="content">
      <div class="content-top">
        <!-- widgets with numbers -->
        <widget class="widget__numbers-container">
          <card
            v-if="!isLoading && isNotEmpty(unitsCounter)"

            title="Units"
            class="card--number"
            :titleStyles="{ fontSize: '24px' }"
            :value="unitsCounter.currentValue"
            :valueStyles="{ fontSize: '48px' }"
          />

          <card
            v-if="!isLoading && isNotEmpty(usersCounter)"

            title="Users"
            class="card--number"
            :titleStyles="{ fontSize: '24px' }"
            :value="usersCounter.currentValue"
            :valueStyles="{ fontSize: '48px' }"
          />

          <card
            v-if="!isLoading && isNotEmpty(onlineCounter)"

            title="Users Online"
            class="card--number"
            :titleStyles="{ fontSize: '24px' }"
            :value="onlineCounter.currentValue"
            :valueStyles="{ fontSize: '48px' }"
          />
        </widget>

        <!-- logo with text -->
        <center
          class="content-center"
          :show-text="!isLoading && isNotEmpty(unitsCounter)"
          label="Units"
          :value="unitsCounter.currentValue"
        />
      </div>

      <!-- charts -->
      <div class="content-bottom">
        <div class="chart">
          <widget class="chart__content">
            <div class="chart__inner">
              <chart
                style="height: 100%;"
                :chartdata="leftChart"
                :options="leftChartOptions"
              />
            </div>
          </widget>
        </div>

        <div class="chart">
          <widget class="chart__content">
            <div class="chart__inner">
              <chart
                style="height: 100%;"
                :chartdata="premiumChart"
                :options="premiumChartOptions"
              />
            </div>
          </widget>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import mqtt from 'mqtt'

import Center from './components/Center'
import Widget from './components/Widget'
import Card from './components/Card'
import Chart from './components/Chart'

const DASHBOARD_TOPIC = 'public/platform/statbox'

export default {
  name: 'app',

  components: {
    Center,
    Widget,
    Card,
    Chart
  },

  data () {
    return {
      isDangerous: false,
      isLoading: true,
      start: 0,
      end: 0,

      unitsCounter: {},
      usersCounter: {},
      tokensCounter: {},
      onlineCounter: {},
      premiumUnitsCount: '?',

      activeUnitsChartData: {
        x: [],
        y: []
      },
      activeTokensChartData: {
        x: [],
        y: [],
      },
      premiumChartData: {
        x: [],
        y: []
      }
    }
  },

  async created () {
    let token

    const localData = localStorage.getItem('statdata')
    if (localData) {
      this.updateStatData(JSON.parse(localData))
    }

    try {
      const response = await axios.get('/token')
      token = response.data;
      localStorage.setItem('token', token)
    } catch (e) {
      token = localStorage.getItem('token')
    } finally {
      if (token) {
        const mqttClient  = mqtt.connect('wss://mqtt.flespi.io:443', {
          clientId: 'dashboard' + Math.random().toString(16).substr(2, 8),
          username: token,
          protocolId: 'MQTT',
          protocolVersion: 5
        })

        mqttClient.on('connect', function () {
          mqttClient.subscribe(DASHBOARD_TOPIC, (error) => {
            if (!error) {
              console.log('mqttClient.subscribe success')
            } else {
              console.error(`mqttClient.subscribe error ${error}`)
            }
          })
        })

        mqttClient.on('message', (topic, message) => {
          if (topic === DASHBOARD_TOPIC) {
            const data = JSON.parse(message.toString())
            localStorage.setItem('statdata', JSON.stringify(data))

            this.updateStatData(data)
          }
        })

        mqttClient.on('disconnect', () => {
          console.error('mqttClient.disconnect')
        })

        mqttClient.on('close', () => {
          console.error('mqttClient.close')
        })
      } else {
        this.isDangerous = true
      }
    }
  },

  computed: {
    leftMinChart() {
      const min0 = Math.min(...this.activeUnitsChartData.y)
      const min1 = Math.min(...this.activeTokensChartData.y)
      return Math.min(min0, min1)
    },

    leftMaxChart() {
      const max0 = Math.max(...this.activeUnitsChartData.y)
      const max1 = Math.max(...this.activeTokensChartData.y)
      return Math.max(max0, max1)
    },

    leftChart() {
      return {
        labels: this.activeUnitsChartData.x.map(i => new Date(i * 1000)),
        datasets: [
          {
            label: 'Active Users',
            borderColor: '#bad2c1',
            backgroundColor: '#bad2c166',
            data: this.activeTokensChartData.x.map((x, index) => {

              return {
                t: new Date(x * 1000),
                y: this.activeTokensChartData.y[index]
              }
            })
          },
          {
            label: 'Active Units',
            borderColor: '#ffcc17',
            backgroundColor: '#ffcc1766',
            data: this.activeUnitsChartData.x.map((x, index) => {
              return {
                t: new Date(x * 1000),
                y: this.activeUnitsChartData.y[index]
              }
            })
          }
        ]
      }
    },

    leftChartOptions() {
      const min = this.leftMinChart
      const max = this.leftMaxChart

      return {
        legend: {
          display: true,
          labels: {
            fontColor: '#FFFFFF',
            fontSize: 16,
            usePointStyle: true
          }
        },

        scales: {
          yAxes: [
            {
              ticks: {
                min: (min / max) * min,
                fontColor: '#ffffff',
                callback: function(label) {
                  return Math.round(label / 1000) + 'k'
                }
              }
            },
          ],
          xAxes: [{
            type: 'time',
            time: {
              unit: 'day'
            },
            ticks: {
              fontColor: '#ffffff',
              autoSkip: true,
              maxTicksLimit: 7
            }
          }]
        }
      }
    },

    premiumChart() {
      return {
        datasets: [
          {
            label: 'Premium Units — ' + this.premiumUnitsCount,
            borderColor: '#ffcc17',
            backgroundColor: '#ffcc1766',
            data: this.premiumChartData.x.map((x, index) => {
              return {
                t: new Date(x * 1000),
                y: this.premiumChartData.y[index]
              }
            })
          }
        ]
      }
    },

    premiumChartOptions() {
      return {
        legend: {
          display: true,
          labels: {
            fontColor: '#FFFFFF',
            fontSize: 16,
            usePointStyle: true
          }
        },

        scales: {
          yAxes: [
            {
              ticks: {
                min: 0,
                fontColor: '#ffffff'
              }
            },
          ],
          xAxes: [{
            type: 'time',
            time: {
              unit: 'day'
            },
            ticks: {
              fontColor: '#ffffff',
              autoSkip: true,
              maxTicksLimit: 7
            }
          }]
        }
      }
    }
  },

  methods: {
    updateStatData(data) {
      this.start = data.start
      this.end = data.end

      const now = (+new Date()) / 1000
      if (now - data.end > 60 * 60) {
        this.isDangerous = true
      } else {
        this.isDangerous = false
      }

      this.$set(this, 'usersCounter', data.usersCounter)
      this.$set(this, 'unitsCounter', data.unitsCounter)
      this.$set(this, 'onlineCounter', data.onlineCounter)
      this.$set(this, 'tokensCounter', data.tokensCounter)

      this.$set(this, 'activeUnitsChartData', data.activeUnitsChart)
      this.$set(this, 'activeTokensChartData', data.activeTokensChart)
      if (data.premiumChart) {
        this.$set(this, 'premiumChartData', data.premiumChart)

        this.$set(this, 'premiumUnitsCount', data.premiumChart.y[data.premiumChart.y.length - 1])
      }

      this.isLoading = false
    },

    speed(delta) {
      return Math.round(delta / ((this.end - this.start) / 3600))
    },

    isNotEmpty(obj) {
      return obj && Object.keys(obj).length > 0
    }
  }
}
</script>

<style>
:root {
  --app-margin: 24px;
  --column-width: 360px;
  --element-width: calc((100% - var(--app-margin)) / 2);
}

html,
body {
  height: 100%;
  margin: 0;
  padding: 0;
  font-family: 'Roboto', sans-serif;
  font-size: 16px;
}

body {
  background-color: #000000;
  color: #ffffff;
  line-height: 1;
  user-select: none;
}

img {
  max-width: 100%;
  max-height: 100%;
  height: auto;
}

#app {
  height: 100%;
}

.dangerous {
  position: absolute;
  top: 15px;
  right: 15px;
  z-index: 999;

  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  width: 40px;
  height: 40px;
  transform: rotate(45deg);

  background-color: #f12323;
  color: #ffffff;
  font-size: 30px;
  font-weight: bold;

  box-shadow:
    0 16px 24px rgba(55, 71, 79, 0.08),
    0 8px 8px rgba(55, 71, 79, 0.12);
}

.dangerous__text {
  display: block;
  transform: rotate(-45deg);
}

.plus {
  color: #53995B;
}

.minus {
  color: #F12323;
}

.center-wrapper {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  margin: 0 var(--app-margin);
  padding: 0 var(--column-width);
}
/* 360px * 3 + 24px * 2 = 1128px */
@media (max-width: 1128px) {
  .center-wrapper {
    display: none;
  }
}

.content {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  margin: var(--app-margin);
  display: flex;
  flex-direction: column;
  justify-content: space-between;
}

.content-top {
  display: flex;
  flex-direction: row-reverse;
}
/* 360px * 2 + 24px * 2 = 768px */
@media (max-width: 768px) {
  .content-top {
    flex-direction: column-reverse;
    align-items: center;
  }
}

.widget__numbers-container {
  display: flex;
  flex-direction: column;
  width: var(--column-width);
}
@media (max-width: 1128px) {
  .widget__numbers-container {
    width: var(--element-width);
  }
}
@media (max-width: 768px) {
  .widget__numbers-container {
    flex-direction: row;
    flex-wrap: wrap;
    width: 100%;
  }
}

.card--number {
  margin: 25px;
  flex-grow: 1;
  flex-shrink: 0;
}

.content-center {
  display: none;
}
@media (max-width: 1128px) {
  .content-center {
    display: flex;
    width: var(--element-width);
  }
}
@media (max-width: 768px) {
  .content-center {
    width: 72px;
  }
}

.content-bottom {
  display: flex;
  justify-content: space-between;
  flex-wrap: wrap;
}

.chart {
  width: var(--column-width);
}
@media (max-width: 1128px) {
  .chart {
    width: var(--element-width);
    margin-top: var(--app-margin);
  }
}
@media (max-width: 768px) {
  .chart {
    width: 100%;
  }
}

.chart__content {
  padding-bottom: 75%;
}

.chart__inner {
  position: absolute;
  width: 100%;
  height: 100%;
}

.chart__title {
  position: absolute;
  left: 50%;

  margin: 0;
  font-weight: bold;
  transform: translateX(-50%);
}
</style>
